/**
 * NA-309: lets a signed-in author change their own password.
 *
 * Built on ModalShell, so the focus trap, Escape handling and focus restore come
 * from the shell rather than being reimplemented here. Stays mounted while
 * closed (ModalShell's contract) so focus returns to the avatar menu button that
 * opened it — which is also why AuthoringShell renders this as a sibling of the
 * profile menu rather than inside the popover: the popover unmounts on route
 * change and would take the dialog's focus bookkeeping with it.
 *
 * Split in two on purpose. ModalShell renders no children while closed, so
 * putting the form and its mutation in an inner component means the request hook
 * only exists while the dialog is open. That keeps AuthoringShell — which is
 * closed almost always — from permanently mounting a mutation, and it makes each
 * opening start from a fresh mount, so there is no reset-on-open bookkeeping to
 * get wrong.
 *
 * The password inputs deliberately carry no autoFocus. The shell already focuses
 * the first focusable child, and an autoFocus fires during commit — before the
 * shell captures the previously focused element — which would make it restore
 * focus to an input it is about to unmount.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { adminField, adminFocusable, adminText, adminTokens } from '../admin-ui';
import { ModalShell } from './ModalShell';
import { ModalFooter } from './ModalFooter';
import { useChangePasswordMutation, type UserApiError } from '../api/userApi';

/**
 * Mirrors the server rule (author-api `src/validation/password.ts`). The server
 * is authoritative and re-checks this; the copy here only spares the user a
 * round trip. If the server rule changes, this is the matching edit.
 */
const MIN_PASSWORD_LENGTH = 12;

/** How long the confirmation stays up before the dialog closes itself. */
const SUCCESS_CLOSE_DELAY_MS = 1500;

const MESSAGES = {
  currentRequired: 'Current password is required',
  newRequired: 'New password is required',
  confirmRequired: 'Please confirm your new password',
  tooShort: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
  mismatch: 'New password and confirmation do not match',
  success: 'Your password has been changed.',
  unexpected: 'Could not change your password. Please try again.',
} as const;

type FieldKey = 'current' | 'new' | 'confirm';

interface FormError {
  /** Field to focus and mark, or null for a form-level problem. */
  field: FieldKey | null;
  message: string;
}

export interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const titleId = useId();
  // Lifted out of the form because ModalShell owns Escape and the backdrop, and
  // neither may abandon a request that is already in flight.
  const [isBusy, setIsBusy] = useState(false);

  const handleClose = useCallback(() => {
    if (isBusy) return;
    onClose();
  }, [isBusy, onClose]);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabelledBy={titleId}
      width="440px"
      disableBackdropClose={isBusy}
      presentation="admin"
    >
      <ChangePasswordForm titleId={titleId} onClose={onClose} onBusyChange={setIsBusy} />
    </ModalShell>
  );
}

interface ChangePasswordFormProps {
  titleId: string;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}

function ChangePasswordForm({ titleId, onClose, onBusyChange }: ChangePasswordFormProps) {
  const styles = useMemo(() => createStyles(), []);
  const currentId = useId();
  const newId = useId();
  const confirmId = useId();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<FormError | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const currentRef = useRef<HTMLInputElement | null>(null);
  const newRef = useRef<HTMLInputElement | null>(null);
  const confirmRef = useRef<HTMLInputElement | null>(null);

  const focusField = useCallback((field: FieldKey | null) => {
    if (field === 'current') currentRef.current?.focus();
    else if (field === 'new') newRef.current?.focus();
    else if (field === 'confirm') confirmRef.current?.focus();
  }, []);

  const fail = useCallback(
    (next: FormError) => {
      setError(next);
      focusField(next.field);
    },
    [focusField]
  );

  const { mutate, isPending } = useChangePasswordMutation({
    onSuccess: () => {
      setSucceeded(true);
      setError(null);
      // Nothing sensitive should outlive a successful change.
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (apiError: UserApiError) => {
      // A rejected current password is the only failure that identifies a
      // specific field, and the only one whose value is worth clearing — the
      // others the user got right and must not lose.
      if (isCurrentPasswordRejection(apiError)) {
        setCurrentPassword('');
        fail({ field: 'current', message: apiError.message });
        return;
      }

      if (isPasswordRuleRejection(apiError)) {
        fail({ field: 'new', message: apiError.message });
        return;
      }

      // Transport failures, 429s and 5xx: keep every field so the user can
      // retry without retyping, and never imply the password was wrong.
      fail({ field: null, message: apiError.message || MESSAGES.unexpected });
    },
  });

  // Let the shell know whether a request is in flight, and clear that on unmount
  // so a reopened dialog never starts out believing it is busy.
  useEffect(() => {
    onBusyChange(isPending);
    return () => onBusyChange(false);
  }, [isPending, onBusyChange]);

  // Close on our own once the confirmation has been readable for a moment.
  useEffect(() => {
    if (!succeeded) return undefined;
    const timer = setTimeout(onClose, SUCCESS_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [succeeded, onClose]);

  const handleSubmit = useCallback(() => {
    // A submission already in flight must not be doubled up.
    if (isPending || succeeded) return;

    setError(null);

    if (currentPassword.length === 0) {
      fail({ field: 'current', message: MESSAGES.currentRequired });
      return;
    }
    if (newPassword.length === 0) {
      fail({ field: 'new', message: MESSAGES.newRequired });
      return;
    }
    if (confirmPassword.length === 0) {
      fail({ field: 'confirm', message: MESSAGES.confirmRequired });
      return;
    }
    // Length before match, so "short AND mismatched" always reports the same
    // thing for the same input.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      fail({ field: 'new', message: MESSAGES.tooShort });
      return;
    }
    if (newPassword !== confirmPassword) {
      fail({ field: 'confirm', message: MESSAGES.mismatch });
      return;
    }

    mutate({ currentPassword, newPassword });
  }, [confirmPassword, currentPassword, fail, isPending, mutate, newPassword, succeeded]);

  const handleCancel = useCallback(() => {
    if (isPending) return;
    onClose();
  }, [isPending, onClose]);

  // The <form> below exists for autofill scoping, not for submission — the
  // submit control lives in ModalFooter and is a plain button. Browsers can
  // still implicitly submit a form, which would navigate and wipe the fields,
  // so swallow it. (Enter-to-submit is deliberately not wired up here: it does
  // not work today and is not part of NA-309.)
  const handleFormSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  }, []);

  const fieldStyle = (field: FieldKey): CSSProperties => ({
    ...styles.input,
    ...(error?.field === field ? styles.inputInvalid : {}),
  });

  return (
    <>
      {/*
        A real <form> rather than a bare <div>: browsers group inputs that are
        not inside a form into one synthetic document-wide unit, so the password
        manager was pairing these password fields with the first plausible text
        input it found elsewhere on the page — filling the saved email into the
        Lessons list search box behind the dialog. Wrapping them puts them in
        their own parsing unit, which takes those search inputs out of the running.
      */}
      <form style={styles.body} onSubmit={handleFormSubmit}>
        <h3 id={titleId} style={styles.title}>Change Password</h3>

        {succeeded ? (
          <p role="status" style={styles.success}>{MESSAGES.success}</p>
        ) : (
          <>
            <label htmlFor={currentId} style={styles.label}>Current password</label>
            <input
              id={currentId}
              ref={currentRef}
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              style={fieldStyle('current')}
              disabled={isPending}
              aria-invalid={error?.field === 'current' || undefined}
            />

            <label htmlFor={newId} style={styles.label}>New password</label>
            <input
              id={newId}
              ref={newRef}
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              style={fieldStyle('new')}
              disabled={isPending}
              aria-invalid={error?.field === 'new' || undefined}
            />

            <label htmlFor={confirmId} style={styles.label}>Confirm new password</label>
            <input
              id={confirmId}
              ref={confirmRef}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              style={fieldStyle('confirm')}
              disabled={isPending}
              aria-invalid={error?.field === 'confirm' || undefined}
            />

            <p style={styles.hint}>
              Must be at least {MIN_PASSWORD_LENGTH} characters.
            </p>
          </>
        )}

        {error && <p role="alert" style={styles.error}>{error.message}</p>}
      </form>

      <ModalFooter
        presentation="admin"
        onCancel={handleCancel}
        cancelDisabled={isPending}
        cancelLabel={succeeded ? 'Close' : 'Cancel'}
        actions={
          succeeded
            ? []
            : [
                {
                  label: isPending ? 'Changing...' : 'Change Password',
                  onClick: handleSubmit,
                  disabled: isPending,
                  ariaLabel: 'Change password',
                  ariaBusy: isPending,
                  variant: 'primary',
                },
              ]
        }
      />
    </>
  );
}

/**
 * The server reports a rejected current password as a 400 whose message names
 * it. Matching on the message is unlovely, but the endpoint returns 400 for
 * several distinct reasons and the alternative — a machine-readable error code —
 * would mean changing the response contract of an endpoint this change is also
 * introducing, for one caller. Revisit if a second caller appears.
 */
function isCurrentPasswordRejection(error: UserApiError): boolean {
  return error.status === 400 && /current password/i.test(error.message);
}

function isPasswordRuleRejection(error: UserApiError): boolean {
  return error.status === 400 && /at least \d+ characters/i.test(error.message);
}

// Opened only from the shell's profile menu, so it uses the admin frame.
function createStyles(): Record<string, CSSProperties> {
  const a = adminTokens;
  return {
    body: {
      padding: a.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      // This element is a <form>; some UA stylesheets give forms a bottom
      // margin, which would open a gap above the footer.
      margin: 0,
    },
    title: {
      ...adminText.sectionTitle,
      margin: `0 0 ${a.spacing.lg} 0`,
    },
    label: {
      ...adminField.label,
      marginBottom: '6px',
    },
    input: {
      ...adminFocusable,
      width: '100%',
      height: '40px',
      padding: `0 ${a.spacing.md}`,
      fontSize: a.typography.body.size,
      fontFamily: a.typography.fontFamily,
      // Long-hand on purpose: `inputInvalid` swaps only borderColor, and mixing
      // that with a `border` shorthand makes React drop the override on rerender.
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: a.border.control,
      borderRadius: a.radius.control,
      marginBottom: a.spacing.lg,
      backgroundColor: a.surface.panel,
      color: a.text.primary,
      boxSizing: 'border-box',
    },
    inputInvalid: {
      borderColor: a.status.error.text,
    },
    hint: {
      margin: '0',
      fontSize: a.typography.label.size,
      color: a.text.secondary,
      lineHeight: 1.5,
    },
    error: {
      margin: `${a.spacing.md} 0 0 0`,
      fontSize: a.typography.body.size,
      color: a.status.error.text,
      lineHeight: 1.5,
    },
    success: {
      margin: 0,
      fontSize: a.typography.body.size,
      color: a.text.primary,
      lineHeight: 1.5,
    },
  };
}
